import React, { forwardRef } from "react";

import FaanLogo from "../../../assets/img/faan-logo.png";
import { formatDateTime, formatKctCode } from "../../../helper/helper";
import { SpecialTokenResponse } from "../../../services/specialToken";

interface SpecialTokenReceiptProps {
  tokenData: SpecialTokenResponse;
}

const SpecialTokenReceipt = forwardRef<HTMLDivElement, SpecialTokenReceiptProps>(
  ({ tokenData }, ref) => {
    const isClearTamper = tokenData.tokenType?.trim().toLowerCase() === "clear tamper";
    const keyChangeTokens = [
      ["KCT1", tokenData.tokenDec_1 || tokenData.tokenDec || ""],
      ["KCT2", tokenData.tokenDec_2 || tokenData.tokenHex || ""],
    ].filter(([, token]) => token);
    const detailsData = [
      { key: "Customer’s Name", value: tokenData.customerName || "" },
      { key: "Address", value: tokenData.address || "" },
      { key: "Meter No", value: tokenData.meterNo || "" },
      { key: "Reason", value: tokenData.reason || "Nil" },
      { key: "Operator ID", value: tokenData.operatorId || "" },
      { key: "Printed on", value: formatDateTime(tokenData.printedOn) },
      { key: "Transaction Date", value: formatDateTime(tokenData.transactionDate) },
    ];

    return (
      <div
        ref={ref}
        id="special-token-receipt"
        className="mx-auto w-[320px] rounded-[18px] bg-[#F3FAF7] p-3 max-[600px]:w-[160px] max-[600px]:rounded-[9px] max-[600px]:p-1"
        style={{ fontFamily: "Aptos, Arial, sans-serif" }}
      >
        <div className="rounded-[12px] bg-white px-3 pb-4 pt-4 max-[600px]:rounded-[6px] max-[600px]:px-1.5 max-[600px]:pb-2 max-[600px]:pt-2">
          <div className="mb-3 flex justify-center max-[600px]:mb-1.5">
            <img src={FaanLogo} alt="FAAN" className="h-6 w-auto object-contain max-[600px]:h-[13px]" />
          </div>
          <div className="text-center">
            <p className="m-0 text-[12px] font-medium leading-4 text-[#222] max-[600px]:text-[6px] max-[600px]:leading-[8px]">
              {tokenData.tokenType || "Engineering"} Token
            </p>
            <p className="mb-0 mt-1 text-[11px] leading-[14px] text-[#777] max-[600px]:mt-0.5">
              {formatDateTime(tokenData.transactionDate)}
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-2 max-[600px]:mt-2 max-[600px]:gap-1 items-center">
            {isClearTamper ? (
              <div className="flex h-[32px] w-fit max-w-full items-center justify-center rounded-[6px] bg-[#F1F1F1] px-2 max-[600px]:h-[20px] max-[600px]:rounded-[3px] max-[600px]:px-1">
                <span
                  data-pdf-token-value
                  className="block text-center text-[12px] font-semibold leading-[12px] text-[#222] whitespace-nowrap max-[600px]:text-[6px] max-[600px]:leading-[6px]"
                >
                  {formatKctCode(tokenData.tokenDec || "")}
                </span>
              </div>
            ) : (
              keyChangeTokens.map(([label, code]) => (
                <div key={label} className="flex w-full items-center justify-center gap-1 max-[600px]:gap-1">
                  <span
                    data-pdf-kct-label
                    className="flex h-[32px] w-[35px] shrink-0 items-center justify-center text-center text-[10px] font-semibold text-[#222] max-[600px]:h-[20px] max-[600px]:w-[17px] max-[600px]:text-[5px]"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "10px", transform: "translateY(-1.2px)" }}
                  >
                    {label}
                  </span>
                  <span className="flex h-[32px] min-w-0 max-w-fit flex-1 items-center justify-center rounded-[6px] bg-[#F1F1F1] px-2 max-[600px]:h-[20px] max-[600px]:rounded-[3px] max-[600px]:px-1">
                    <span
                      data-pdf-token-value
                      className="flex items-center justify-center text-center text-[12px] font-semibold text-[#222] whitespace-nowrap max-[600px]:text-[6px]"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "1" }}
                    >
                      {formatKctCode(code)}
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="mt-6 w-full max-[600px]:mt-2.5">
            {detailsData.map((item) => (
              <div key={item.key} className="flex min-h-[24px] items-center justify-between gap-2 border-b border-[#E5E5E5] py-1 max-[600px]:gap-1">
                <span className="shrink-0 text-[11px] leading-[14px] text-[#5F5F5F]">{item.key}</span>
                <span className="max-w-[62%] break-words text-right text-[11px] leading-[14px] text-[#303030]">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

SpecialTokenReceipt.displayName = "SpecialTokenReceipt";

export default SpecialTokenReceipt;